import React, { useEffect, useState } from 'react';
import { useAuth } from '../redux/hooks/useAuth';
import { Box, Typography, Alert, CircularProgress, Fade, keyframes } from '@mui/material';
import { useThemeColors } from '../theme';
import AnimatedLogo from './AnimatedLogo';

const AuthInitializer = ({ children }) => {
  const { initialized, initializeAuth } = useAuth();
  const [localInitialized, setLocalInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Initializing...');
  const colors = useThemeColors();

  // Safety check for theme loading - fallback colors if theme isn't ready yet
  const safeColors = {
    gradient: {
      background: colors?.gradient?.background || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    text: {
      primary: colors?.text?.primary || '#ffffff'
    },
    surface: {
      overlay: colors?.surface?.overlay || 'rgba(255, 255, 255, 0.1)',
      subtle: colors?.surface?.subtle || 'rgba(255, 255, 255, 0.08)',
      muted: colors?.surface?.muted || 'rgba(255, 255, 255, 0.06)',
      glass: colors?.surface?.glass || 'linear-gradient(45deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))'
    },
    border: {
      subtle: colors?.border?.subtle || 'rgba(255,255,255,0.2)',
      primary: colors?.border?.primary || 'rgba(255,255,255,0.4)',
      secondary: colors?.border?.secondary || 'rgba(255,255,255,0.2)'
    },
    accent: {
      primary: colors?.accent?.primary || 'rgba(255,255,255,0.7)',
      secondary: colors?.accent?.secondary || 'rgba(255,255,255,0.3)'
    },
    shadow: {
      medium: colors?.shadow?.medium || '0 8px 32px rgba(0,0,0,0.2)',
      color: colors?.shadow?.color || 'rgba(0,0,0,0.3)'
    }
  };

  console.log('🔄 AuthInitializer: Component mounted/re-rendered', { 
    initialized, 
    localInitialized, 
    error, 
    status 
  });

  // Initialize authentication only once on mount
  useEffect(() => {
    let isMounted = true;
    
    const init = async () => {
      try {
        console.log('🔄 AuthInitializer: Starting initialization...');
        setStatus('Checking authentication status...');
        
        // Call the auth initialization
        await initializeAuth();
        
        if (isMounted) {
          console.log('✅ AuthInitializer: Initialization complete');
          setStatus('Setup complete!');
          
          // Add a small delay to show the completion message
          setTimeout(() => {
            if (isMounted) {
              console.log('✅ AuthInitializer: Setting localInitialized to true');
              setLocalInitialized(true);
            }
          }, 1000);
        }
      } catch (err) {
        if (isMounted) {
          console.error('❌ AuthInitializer: Initialization failed:', err);
          setError('Failed to initialize authentication. Please refresh the page.');
          setLocalInitialized(true); // Set to true to stop loading screen
        }
      }
    };

    console.log('🔄 AuthInitializer: Starting initialization process...');
    init();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - only run once on mount

  // Wait for both Redux initialized and local initialization to complete
  const isReady = initialized && localInitialized;

  // Show loading screen during initialization
  if (!isReady) {
    console.log('🔄 AuthInitializer: Showing loading screen with status:', status);
    
    // Animation keyframes
    const pulse = keyframes`
      0% {
        transform: scale(1);
        opacity: 1;
      }
      50% {
        transform: scale(1.05);
        opacity: 0.8;
      }
      100% {
        transform: scale(1);
        opacity: 1;
      }
    `;

    const float = keyframes`
      0%, 100% {
        transform: translateY(0px);
      }
      50% {
        transform: translateY(-10px);
      }
    `;

    const shimmer = keyframes`
      0% {
        background-position: -200px 0;
      }
      100% {
        background-position: calc(200px + 100%) 0;
      }
    `;

    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: safeColors.gradient.background,
        color: safeColors.text.primary,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background animated circles */}
        <Box sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: safeColors.surface.overlay,
          animation: `${float} 3s ease-in-out infinite`,
        }} />
        <Box sx={{
          position: 'absolute',
          top: '70%',
          right: '15%',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: safeColors.surface.subtle,
          animation: `${float} 4s ease-in-out infinite`,
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '20%',
          left: '20%',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: safeColors.surface.muted,
          animation: `${float} 2.5s ease-in-out infinite`,
        }} />

        {/* Main content */}
        <Fade in timeout={800}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            zIndex: 2,
            textAlign: 'center'
          }}>
            {/* HyreX Animated Logo */}
            <Box sx={{
              position: 'relative',
              mb: 4,
              animation: `${pulse} 2s ease-in-out infinite`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Box sx={{
                padding: 2,
                borderRadius: '50%',
                background: safeColors.surface.glass,
                border: `2px solid ${safeColors.border.subtle}`,
                backdropFilter: 'blur(15px)',
                boxShadow: safeColors.shadow.medium,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <AnimatedLogo 
                  size={60}
                  style={{ 
                    filter: `brightness(1.2) drop-shadow(0 4px 8px ${safeColors.shadow.color})` 
                  }}
                />
              </Box>
              
              {/* Rotating ring around logo */}
              <Box sx={{
                position: 'absolute',
                top: '-15px',
                left: '-15px',
                width: 'calc(100% + 30px)',
                height: 'calc(100% + 30px)',
                borderRadius: '50%',
                border: '2px solid transparent',
                borderTop: `2px solid ${safeColors.accent.primary}`,
                borderRight: `2px solid ${safeColors.accent.secondary}`,
                animation: 'spin 3s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }} />
              
              {/* Additional inner ring for more depth */}
              <Box sx={{
                position: 'absolute',
                top: '-5px',
                left: '-5px',
                width: 'calc(100% + 10px)',
                height: 'calc(100% + 10px)',
                borderRadius: '50%',
                border: '1px solid transparent',
                borderBottom: `1px solid ${safeColors.border.primary}`,
                borderLeft: `1px solid ${safeColors.border.secondary}`,
                animation: 'spin 2s linear infinite reverse',
              }} />
            </Box>

            {/* Status text with shimmer effect */}
            {/* <Box sx={{
              position: 'relative',
              mb: 2,
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(
                  90deg,
                  transparent,
                  rgba(255, 255, 255, 0.2),
                  transparent
                )`,
                animation: `${shimmer} 2s infinite`,
              }
            }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: 'white',
                  fontWeight: 500,
                  letterSpacing: '0.5px',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                {status}
              </Typography>
            </Box> */}

            {/* Animated dots */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              mt: 2
            }}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: safeColors.accent.primary,
                    animation: `${pulse} 1.5s ease-in-out infinite`,
                    animationDelay: `${i * 0.2}s`,
                  }}
                />
              ))}
            </Box>

            {/* Progress description */}
            {/* <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255,255,255,0.8)',
                mt: 3,
                maxWidth: '300px',
                lineHeight: 1.6,
                textAlign: 'center'
              }}
            >
              Setting up your personalized experience...
            </Typography> */}
          </Box>
        </Fade>
      </Box>
    );
  }

  // Show error if initialization failed
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: safeColors.gradient.background,
          padding: 2
        }}
      >
        <Alert 
          severity="error" 
          sx={{ 
            maxWidth: 500, 
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          }}
          action={
            <button 
              onClick={() => window.location.reload()} 
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              Refresh Page
            </button>
          }
        >
          <Typography variant="h6" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Render children when initialization is complete
  return children;
};

export default AuthInitializer;
